import type React from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import { shouldReconnectChatWs } from './chatWsReconnectPolicy';

export type ChatWsEventType =
  | 'message'
  | 'contact_share'
  | 'read'
  | 'delivered'
  | 'presence'
  | 'presence_snapshot'
  | 'error'
  | 'pong'
  | 'subscribed'
  | 'notification'
  | 'bulletin_sync'
  | 'profile_sync';

/** User-channel events that must reach subscribeGlobal (no chat room_id). */
const GLOBAL_USER_EVENT_TYPES = new Set<string>([
  'notification',
  'bulletin_sync',
  'profile_sync',
  'presence',
  'presence_snapshot',
]);

export interface ChatWsIncomingMessage {
  type: ChatWsEventType | string;
  data: Record<string, unknown>;
}

type RoomHandler = (event: ChatWsIncomingMessage) => void;
type GlobalHandler = (event: ChatWsIncomingMessage) => void;

interface ChatWebSocketContextValue {
  isConnected: boolean;
  userId: string;
  subscribeRoom: (
    roomId: string,
    chatId: number,
    handler: RoomHandler,
  ) => () => void;
  subscribeGlobal: (handler: GlobalHandler) => () => void;
  sendChatMessage: (payload: {
    chatId: number;
    roomId: string;
    text: string;
    localId: string;
  }) => boolean;
  markChatRead: (payload: { chatId: number; roomId: string }) => boolean;
}

const ChatWebSocketContext = createContext<ChatWebSocketContextValue>({
  isConnected: false,
  userId: '',
  subscribeRoom: () => () => undefined,
  subscribeGlobal: () => () => undefined,
  sendChatMessage: () => false,
  markChatRead: () => false,
});

export const useChatWebSocket = () => useContext(ChatWebSocketContext);

const getWsUrl = (): string => {
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${window.location.host}/ws/chat`;
};

interface ChatWebSocketProviderProps {
  children: React.ReactNode;
  userId: string;
}

export const ChatWebSocketProvider: React.FC<ChatWebSocketProviderProps> = ({
  children,
  userId,
}) => {
  const [isConnected, setIsConnected] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimerRef = useRef<number | null>(null);
  const intentionalCloseRef = useRef(false);
  const everOpenedRef = useRef(false);
  const roomHandlersRef = useRef<Map<string, Set<RoomHandler>>>(new Map());
  const globalHandlersRef = useRef<Set<GlobalHandler>>(new Set());
  const subscribedRoomsRef = useRef<Map<string, number>>(new Map());

  const dispatchRoom = useCallback((roomId: string, event: ChatWsIncomingMessage) => {
    const handlers = roomHandlersRef.current.get(roomId);
    if (!handlers?.size) {
      return;
    }
    for (const handler of handlers) {
      handler(event);
    }
  }, []);

  // r5: снимок онлайна приходит сразу после подключения - часто раньше, чем подписчики
  // (GlobalPresenceProvider) успевают подписаться после isConnected. Держим последний снимок
  // с учетом событий presence и отдаем его каждому новому подписчику.
  const presenceCacheRef = useRef<Set<string> | null>(null);

  const dispatchGlobal = useCallback((event: ChatWsIncomingMessage) => {
    const ev = event as { type?: string; data?: { online_user_ids?: unknown; user_id?: unknown; online?: unknown } };
    if (ev.type === 'presence_snapshot' && Array.isArray(ev.data?.online_user_ids)) {
      presenceCacheRef.current = new Set(ev.data!.online_user_ids.map((id) => String(id)));
    } else if (ev.type === 'presence' && presenceCacheRef.current && ev.data?.user_id != null) {
      const id = String(ev.data.user_id);
      if (ev.data.online) presenceCacheRef.current.add(id);
      else presenceCacheRef.current.delete(id);
    }
    for (const handler of globalHandlersRef.current) {
      handler(event);
    }
  }, []);

  const routeIncoming = useCallback(
    (parsed: ChatWsIncomingMessage) => {
      if (parsed.type === 'pong') {
        return;
      }

      const roomId =
        (parsed.data?.roomId as string | undefined) ||
        (parsed.data?.room_id as string | undefined);

      // Private user events (notifications, bulletin/profile sync) have no room  - 
      // without this branch they were silently dropped and tutor feeds never refreshed.
      if (GLOBAL_USER_EVENT_TYPES.has(String(parsed.type))) {
        dispatchGlobal(parsed);
        return;
      }

      if (parsed.type === 'delivered') {
        dispatchGlobal(parsed);
        if (roomId) {
          dispatchRoom(roomId, parsed);
        }
        return;
      }

      if (parsed.type === 'error') {
        if (roomId) {
          dispatchRoom(roomId, parsed);
        } else {
          for (const rid of roomHandlersRef.current.keys()) {
            dispatchRoom(rid, parsed);
          }
        }
        return;
      }

      if (roomId) {
        dispatchRoom(roomId, parsed);
        return;
      }

      // Fallback: roomless payloads still reach global subscribers.
      dispatchGlobal(parsed);
    },
    [dispatchGlobal, dispatchRoom],
  );

  // Очередь: пока сокет CONNECTING/переподключается, действия (кроме ping) копятся и уходят в onopen.
  const pendingSendRef = useRef<Record<string, unknown>[]>([]);
  const sendJson = useCallback((payload: Record<string, unknown>) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      if (payload.action !== 'ping' && payload.action !== 'subscribe') {
        pendingSendRef.current = [...pendingSendRef.current, payload].slice(-50);
      }
      return false;
    }
    ws.send(JSON.stringify(payload));
    return true;
  }, []);
  const flushPending = useCallback(() => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    const queued = pendingSendRef.current;
    pendingSendRef.current = [];
    for (const payload of queued) ws.send(JSON.stringify(payload));
  }, []);

  const resubscribeAll = useCallback(() => {
    for (const [roomId, chatId] of subscribedRoomsRef.current.entries()) {
      sendJson({ action: 'subscribe', room_id: roomId, chat_id: chatId });
    }
  }, [sendJson]);

  const connect = useCallback((attempt = 0) => {
    if (!userId || userId === '0') {
      return;
    }
    const current = wsRef.current;
    if (
      current &&
      (current.readyState === WebSocket.OPEN || current.readyState === WebSocket.CONNECTING)
    ) {
      return;
    }

    intentionalCloseRef.current = false;
    if (attempt === 0) {
      everOpenedRef.current = false;
    }
    const ws = new WebSocket(getWsUrl());
    wsRef.current = ws;

    ws.onopen = () => {
      everOpenedRef.current = true;
      setIsConnected(true);
      resubscribeAll();
      flushPending();
      attempt = 0;
    };

    ws.onclose = (event) => {
      presenceCacheRef.current = null;
      setIsConnected(false);
      wsRef.current = null;
      if (intentionalCloseRef.current) {
        return;
      }
      if (
        !shouldReconnectChatWs({
          userId,
          closeCode: event.code,
          everOpened: everOpenedRef.current,
        })
      ) {
        return;
      }
      if (reconnectTimerRef.current) {
        window.clearTimeout(reconnectTimerRef.current);
      }

      const delay = Math.min(1000 * Math.pow(2, attempt), 30000);
      const jitter = Math.random() * 1000;
      reconnectTimerRef.current = window.setTimeout(() => connect(attempt + 1), delay + jitter);
    };

    ws.onerror = () => {
      setIsConnected(false);
    };

    ws.onmessage = (raw) => {
      try {
        const parsed = JSON.parse(String(raw.data)) as ChatWsIncomingMessage;
        routeIncoming(parsed);
      } catch {
        // ignore malformed payloads
      }
    };
  }, [userId, routeIncoming, resubscribeAll, flushPending]);

  useEffect(() => {
    connect();
    const pingInterval = window.setInterval(() => {
      sendJson({ action: 'ping' });
    }, 30_000);

    return () => {
      window.clearInterval(pingInterval);
      if (reconnectTimerRef.current) {
        window.clearTimeout(reconnectTimerRef.current);
      }
      intentionalCloseRef.current = true;
      wsRef.current?.close();
      wsRef.current = null;
    };
  }, [connect, sendJson]);

  const subscribeRoom = useCallback(
    (roomId: string, chatId: number, handler: RoomHandler) => {
      if (!roomHandlersRef.current.has(roomId)) {
        roomHandlersRef.current.set(roomId, new Set());
      }
      roomHandlersRef.current.get(roomId)!.add(handler);
      subscribedRoomsRef.current.set(roomId, chatId);
      sendJson({ action: 'subscribe', room_id: roomId, chat_id: chatId });

      return () => {
        const set = roomHandlersRef.current.get(roomId);
        set?.delete(handler);
        if (set && set.size === 0) {
          roomHandlersRef.current.delete(roomId);
          subscribedRoomsRef.current.delete(roomId);
          sendJson({ action: 'unsubscribe', room_id: roomId });
        }
      };
    },
    [sendJson],
  );

  const subscribeGlobal = useCallback((handler: GlobalHandler) => {
    globalHandlersRef.current.add(handler);
    const cached = presenceCacheRef.current;
    if (cached) {
      const snapshot = {
        type: 'presence_snapshot',
        data: { online_user_ids: Array.from(cached) },
      } as unknown as ChatWsIncomingMessage;
      queueMicrotask(() => {
        if (globalHandlersRef.current.has(handler)) handler(snapshot);
      });
    }
    return () => {
      globalHandlersRef.current.delete(handler);
    };
  }, []);

  const sendChatMessage = useCallback(
    (payload: { chatId: number; roomId: string; text: string; localId: string }) =>
      sendJson({
        action: 'send',
        chat_id: payload.chatId,
        room_id: payload.roomId,
        text: payload.text,
        local_id: payload.localId,
      }),
    [sendJson],
  );

  const markChatRead = useCallback(
    (payload: { chatId: number; roomId: string }) =>
      sendJson({
        action: 'mark_read',
        chat_id: payload.chatId,
        room_id: payload.roomId,
      }),
    [sendJson],
  );

  return (
    <ChatWebSocketContext.Provider
      value={{
        isConnected,
        userId,
        subscribeRoom,
        subscribeGlobal,
        sendChatMessage,
        markChatRead,
      }}
    >
      {children}
    </ChatWebSocketContext.Provider>
  );
};
